import { LinkOutlined, SyncOutlined } from '@ant-design/icons';
import {
    AutoComplete,
    Button,
    Card,
    Flex,
    Space,
    Spin,
    Typography,
} from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { PayrollEmployeeCheckDto, ShiftListItemDto } from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';
import { toDollarAmount } from '@clh/util';

import { useSelection } from '../../hooks/use-selection';
import { useToast } from '../../hooks/use-toast';
import { getZealCheckUrl } from '../../payroll-invoicing/zeal-url';
import ObjectPropertyList from '../../record-details/object-property-list';

const { Text } = Typography;

const formatDate = (date: Date) => {
    return DateTime.fromJSDate(new Date(date)).toFormat('MMM dd, yyyy');
};

const formatCurrency = (cents: number | null | undefined) => {
    if (cents == null) return '-';
    return toDollarAmount(cents);
};

function formatCheckOption(check: PayrollEmployeeCheckDto): string {
    const parts: string[] = [];

    if (check.payPeriodStart && check.payPeriodEnd) {
        parts.push(
            `${formatDate(check.payPeriodStart)} – ${formatDate(check.payPeriodEnd)}`
        );
    }

    if (check.checkDate) {
        parts.push(`Check: ${formatDate(check.checkDate)}`);
    }

    if (check.status) {
        parts.push(check.status);
    }

    return parts.join(' | ');
}

export default function PayTab() {
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const api = useApiClient();
    const { notification } = useToast();

    const [editingPayPeriodId, setEditingPayPeriodId] = useState<string | null>(
        null
    );
    const [zealCheckId, setZealCheckId] = useState('');
    const [loading, setLoading] = useState(false);
    const [resyncing, setResyncing] = useState(false);
    const [checkDataMap, setCheckDataMap] = useState<
        Map<string, PayrollEmployeeCheckDto>
    >(new Map());
    const [availableChecks, setAvailableChecks] = useState<
        PayrollEmployeeCheckDto[]
    >([]);
    const [loadingChecks, setLoadingChecks] = useState(false);

    useEffect(() => {
        const fetchChecks = async () => {
            if (!api || !selection.id) return;

            try {
                const checks = await api.payrollControllerGetPaychecks({
                    search: selection.id,
                });

                const newCheckMap = new Map<string, PayrollEmployeeCheckDto>();

                checks?.forEach((check) => {
                    check.payPeriods?.forEach((payPeriod) => {
                        const matchingShiftPayPeriod =
                            selection.payPeriods?.find(
                                (spp) => spp.id === payPeriod.id
                            );
                        if (matchingShiftPayPeriod) {
                            newCheckMap.set(payPeriod.id, check);
                        }
                    });
                });

                setCheckDataMap(newCheckMap);
            } catch (err) {
                notification.error({ message: unwrapError(err) });
            }
        };

        void fetchChecks();
    }, [api, selection]);

    const fetchAvailableChecks = async () => {
        const profileId = selection.nurseProfile?.id;
        if (!api || !profileId) return;

        setLoadingChecks(true);
        try {
            const checks = await api.payrollControllerGetPaychecks({
                profileId,
                forceSync: true,
            });
            setAvailableChecks(checks ?? []);
        } catch (err) {
            notification.error({ message: unwrapError(err) });
        } finally {
            setLoadingChecks(false);
        }
    };

    const handleSetZealCheck = async (payPeriodId: string) => {
        setLoading(true);
        try {
            const result = await api?.payrollControllerSetPayPeriodZealCheck({
                payPeriodId,
                setZealCheckDto: { zealEmployeeCheckId: zealCheckId },
            });

            if (!result) {
                throw new Error('No response from API');
            }

            const updatedPayPeriods = selection.payPeriods?.map((pp) =>
                pp.id === payPeriodId
                    ? { ...pp, zealEmployeeCheckId: result.zealEmployeeCheckId }
                    : pp
            );

            setCheckDataMap((prev) => new Map(prev).set(payPeriodId, result));

            setSelection({ ...selection, payPeriods: updatedPayPeriods });

            notification.success({
                message: 'Zeal check ID updated successfully',
                duration: 3,
            });
            setEditingPayPeriodId(null);
            setZealCheckId('');
        } catch (err) {
            notification.error({
                message: unwrapError(err),
                duration: 3,
            });
        } finally {
            setLoading(false);
        }
    };

    const checkOptions = availableChecks.map((check) => ({
        value: check.zealEmployeeCheckId,
        label: (
            <Flex vertical>
                <Text strong>{formatCheckOption(check)}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {check.zealEmployeeCheckId}
                </Text>
            </Flex>
        ),
    }));

    const handleResync = async () => {
        if (!api || !selection.id) return;
        setResyncing(true);
        try {
            const unlinkedIds =
                await api.payrollControllerResyncShiftZealChecks({
                    shiftId: selection.id,
                });

            if (unlinkedIds.length > 0) {
                const updatedPayPeriods = selection.payPeriods?.map((pp) =>
                    unlinkedIds.includes(pp.id)
                        ? { ...pp, zealEmployeeCheckId: null }
                        : pp
                );
                setSelection({ ...selection, payPeriods: updatedPayPeriods });
                setCheckDataMap((prev) => {
                    const next = new Map(prev);
                    for (const id of unlinkedIds) {
                        next.delete(id);
                    }
                    return next;
                });
                notification.success({
                    message: `Unlinked ${unlinkedIds.length} pay period(s)`,
                    duration: 3,
                });
            } else {
                notification.success({
                    message: 'Resynced — no changes needed',
                    duration: 3,
                });
            }
        } catch (err) {
            notification.error({ message: unwrapError(err), duration: 3 });
        } finally {
            setResyncing(false);
        }
    };

    if (!selection.payPeriods || selection.payPeriods.length === 0) {
        return (
            <Typography.Text type="secondary">
                No pay periods found for this shift
            </Typography.Text>
        );
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Flex justify="space-between" align="center">
                <Typography.Title level={4} style={{ margin: 0 }}>
                    Pay Periods
                </Typography.Title>
                <Button
                    icon={<SyncOutlined />}
                    onClick={() => void handleResync()}
                    loading={resyncing}
                >
                    Resync Zeal Checks
                </Button>
            </Flex>
            {selection.payPeriods.map((payPeriod) => {
                const isEditing = editingPayPeriodId === payPeriod.id;
                const checkData = checkDataMap.get(payPeriod.id);

                return (
                    <Card
                        key={payPeriod.id}
                        title={`${formatDate(
                            payPeriod.startDate
                        )} - ${formatDate(payPeriod.endDate)}`}
                        style={{ marginBottom: 16 }}
                    >
                        <Space
                            direction="vertical"
                            style={{
                                width: '100%',
                                marginBottom: 16,
                            }}
                        >
                            {isEditing ? (
                                <>
                                    <Text type="secondary">
                                        Select a check from the dropdown or
                                        paste a Zeal Check ID directly.
                                    </Text>
                                    <Flex gap={8}>
                                        <AutoComplete
                                            value={zealCheckId}
                                            onChange={setZealCheckId}
                                            onSelect={(value: string) =>
                                                setZealCheckId(value)
                                            }
                                            options={checkOptions}
                                            popupMatchSelectWidth={false}
                                            placeholder="Select or enter Zeal Check ID"
                                            style={{ flex: 1 }}
                                            filterOption={(input, option) =>
                                                (option?.value ?? '')
                                                    .toLowerCase()
                                                    .includes(
                                                        input.toLowerCase()
                                                    )
                                            }
                                            notFoundContent={
                                                loadingChecks ? (
                                                    <Spin size="small" />
                                                ) : null
                                            }
                                        />
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                void handleSetZealCheck(
                                                    payPeriod.id
                                                );
                                            }}
                                            loading={loading}
                                        >
                                            Submit
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setEditingPayPeriodId(null);
                                                setZealCheckId('');
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Flex>
                                </>
                            ) : (
                                <Flex gap={8} align="center">
                                    <Button
                                        onClick={() => {
                                            setEditingPayPeriodId(payPeriod.id);
                                            setZealCheckId(
                                                payPeriod.zealEmployeeCheckId ||
                                                    ''
                                            );
                                            void fetchAvailableChecks();
                                        }}
                                    >
                                        {payPeriod.zealEmployeeCheckId
                                            ? 'Change'
                                            : 'Set'}{' '}
                                        Zeal Check ID
                                    </Button>
                                    {checkData && (
                                        <Button
                                            type="link"
                                            icon={<LinkOutlined />}
                                            href={getZealCheckUrl(checkData)}
                                            target="_blank"
                                        >
                                            Open in Zeal
                                        </Button>
                                    )}
                                </Flex>
                            )}
                        </Space>

                        <ObjectPropertyList
                            values={{
                                minutesWorked: payPeriod.minutesWorked,
                                onCallMinutesWorked:
                                    payPeriod.onCallMinutesWorked,
                                zealEmployeeCheckId:
                                    payPeriod.zealEmployeeCheckId,
                                ...(checkData && {
                                    grossPay: checkData.grossPay,
                                    netPay: checkData.netPay,
                                    overtimePay: checkData.overtimePay,
                                    checkDate: checkData.checkDate,
                                    status: checkData.status,
                                    isApproved: checkData.isApproved,
                                }),
                            }}
                            fields={{
                                minutesWorked: true,
                                onCallMinutesWorked: true,
                                zealEmployeeCheckId: (
                                    checkId: string | null
                                ) =>
                                    checkId ? (
                                        <Text code copyable>
                                            {checkId}
                                        </Text>
                                    ) : (
                                        <Text type="secondary">Not set</Text>
                                    ),
                                ...(checkData && {
                                    grossPay: (cents: number) => (
                                        <>{formatCurrency(cents)}</>
                                    ),
                                    netPay: (cents: number) => (
                                        <>{formatCurrency(cents)}</>
                                    ),
                                    overtimePay: (cents: number | null) => (
                                        <>{formatCurrency(cents)}</>
                                    ),
                                    checkDate: (date: Date) => (
                                        <>{formatDate(date)}</>
                                    ),
                                    status: true,
                                    isApproved: (approved: boolean) => (
                                        <>{approved ? 'Yes' : 'No'}</>
                                    ),
                                }),
                            }}
                        />
                    </Card>
                );
            })}
        </Space>
    );
}
